import type { FarmTwistView, SupplySlot } from "@highmarch/app";
import { checkHtml, wellHtml } from "../components";
import { icon } from "../iconsKit";
import { supplyIconUrl } from "./supplyArt";

/**
 * The pre-battle rows (MP-06, MP-25) on the hub's battle card and the campaign map's level panel: the four supply
 * wells (what is held, a tap arms or disarms one for the next battle — one of each) with, on the battle card only, a
 * small link to the Shop's Supplies (no price here: the battle card is pre-level and in the hub, so the never list
 * allows it, but the gem purchase itself stays in the Shop); and the farm twist's switch for a replay of a cleared
 * level. Strings only; features/supplies.ts handles the taps (delegated) and redraws the pressed state in place.
 */

/**
 * The four wells and the Shop link. `shopLink` false hides it (the map panel, the quiet after a loss, not open yet);
 * `lead` goes before the wells (the battle card's compact twist switch on a short screen).
 */
export function suppliesRowHtml(slots: readonly SupplySlot[], shopLink: boolean, lead = ""): string {
  const held = slots.some((s) => s.have > 0);
  const wells = lead + slots.map((s) => supplyWellHtml(s)).join("");
  const link = shopLink ? `<button type="button" class="supshop" aria-label="Get supplies in the Shop">${icon("shop", 16)}</button>` : "";
  return `<div class="suprow${held ? " held" : ""}${lead ? " twisted" : ""}"><span class="suplbl"><b class="disp">Supplies</b></span><div class="supwells">${wells}</div>${link}</div>`;
}

/** One well: the supply's art, how many are held, a check while it is armed; dim when none is held. */
export function supplyWellHtml(s: SupplySlot): string {
  const state = s.armed ? "taken for the next battle" : s.have > 0 ? "tap to take it" : "none held";
  const well = wellHtml({
    art: supplyIconUrl(s.item.id), size: "sm", tag: "button", count: s.have > 0 ? s.have : "", dim: s.have <= 0,
    className: `supwell${s.armed ? " on" : ""}`, label: `${s.item.name}: ${s.item.blurb}. ${s.have} held, ${state}`, data: `data-sup="${s.item.id}" aria-pressed="${s.armed}"`,
  });
  return s.armed ? well.replace(/<\/button>$/, `${checkHtml("supchk")}</button>`) : well;
}

/** The twist switch: the day's Trial modifier for this replay and its bonus; `farm` names it for the FARM button. */
export function twistRowHtml(t: FarmTwistView, farm: boolean): string {
  const m = t.modifier;
  // the modifier's name only; its rule is said when the switch is flipped on (features/supplies.ts toasts data-say)
  const say = `${m.name}: ${m.blurb} +${t.bonusPercent}% gold.`;
  return `<button type="button" class="suptwist tw-full${t.on ? " on" : ""}" aria-pressed="${t.on}" aria-label="${farm ? "Farm twist" : "Twist"}: ${m.name}, ${m.blurb} Plus ${t.bonusPercent} percent gold. ${t.on ? "On" : "Off"}" data-say="${say}">`
    + `<span class="tw-ic">${icon("flame", 18)}</span><span class="tw-t"><b>${m.name}</b></span>`
    + `<span class="tw-bonus disp">+${t.bonusPercent}%<small class="tw-u">gold</small></span><span class="tw-sw" aria-hidden="true"><i></i></span></button>`;
}

/**
 * The twist switch as one well (the battle card, beside the supply wells — UI pass 2026-10-21: on every screen, so the
 * card reads at a glance): the flame, "+25%" on its cap; a tap flips it and says which twist it is.
 */
export function twistMiniHtml(t: FarmTwistView): string {
  const m = t.modifier, say = `${m.name}: ${m.blurb} +${t.bonusPercent}% gold.`;
  return `<button type="button" class="k-well sm suptwist tw-mini${t.on ? " on" : ""}" aria-pressed="${t.on}" aria-label="Farm twist, ${say} ${t.on ? "On" : "Off"}" data-say="${say}">`
    + `<span class="tw-ic">${icon("flame", 20)}</span><i class="k-cap">+${t.bonusPercent}%</i></button>`;
}
